// Story 7.11 (ADO-1142) — substitute suggestions in a Zustand store (AC1/AC3).
//
// project-context React rules (ADR-004): Zustand for all client-side global
// state. Substitutes are fetched LAZILY and keyed by SKU: a card loads its
// substitutes ONLY when the out-of-stock trigger fires (Deviation D-4) — never
// eagerly for every card in a grid. The store caches per SKU so the panel open
// and the "Substitutes Available" indicator share ONE fetch.
//
// The indicator (AC1) and the AC3 "no section" state both derive from the loaded
// entry: `loaded` + a non-empty list → indicator + panel; `loaded` + empty →
// render nothing. Availability-based exclusion of a substitute that is ITSELF out
// of stock (OQ-5, D-10) is applied by `ProductCard` from the availability store
// (`useCandidateAvailability`) before it hands the list to `SubstitutesPanel`, not
// here — this store holds the server's account-type-eligible candidates, OVER-FETCHED
// so that exclusion can still fill the ≤3 display cap (Story 7-FU-I, AC2).
//
// Enabler E7-C (ADO-2274), D-1: each request names the selected ordering entry, and
// the store is reset when the entry changes (`startCatalogueEntryResets`). The depot
// stamp and its refetch stay as they are: a depot switch moves the entry too.

import { create } from 'zustand';
import { fetchSubstitutes, type SubstituteProduct } from '../api/substitutes.client';
import { resolveSessionDepotId } from '../availability/session-depot';
import { resolveCatalogueEntryKey } from '../ordering/catalogueEntry';
import { createRetryRegister, MAX_RETRY_ATTEMPTS, nextBackoffMs } from '../shared/bounded-retry';

export type SubstitutesPhase = 'loading' | 'loaded' | 'error';

export interface SubstitutesEntry {
  readonly phase: SubstitutesPhase;
  /** The server's account-type-eligible candidates — OVER-FETCHED up to
   *  `MAX_SUBSTITUTE_CANDIDATES` (10, get-substitutes.service.ts); the ≤3 display cap is
   *  applied client-side AFTER OOS-exclusion (Story 7-FU-I, AC2 — `MAX_SUBSTITUTES_DISPLAYED`
   *  in ProductCard.tsx). Empty on `loaded` → AC3 no section. */
  readonly substitutes: readonly SubstituteProduct[];
  /** The depot these candidates were fetched at (`null` = no active depot). The store is
   *  DEPOT-AWARE: `fetchSubstitutes` is depot-scoped and `findSubstituteLinks` applies a
   *  depot-specific-wins dedupe, so a candidate list is only valid for the depot it was
   *  fetched at. A header depot switch flips the active depot WITHOUT resetting this store,
   *  and entries are keyed by SKU alone — so `load()` compares this stamp to the current depot
   *  and refetches when they differ, rather than early-returning on a `loaded`/`loading` entry
   *  and leaving the OOS filter to evaluate NEW-depot stock against OLD-depot candidates. */
  readonly depotId: string | null;
  /** `true` on a `loading` entry that REPLACED a loaded candidate list because the depot
   *  changed — i.e. the card was already offering substitutes and is now refetching them at
   *  the new depot. Set only in that case, so a consumer can hold a CTA that was ALREADY on
   *  screen in a pending state through the round trip instead of flashing it away, while a
   *  FIRST load (nothing on screen yet) still shows nothing until it resolves. */
  readonly refetching?: boolean;
}

interface SubstitutesStoreState {
  /** Per-SKU cache. Absent = never requested (no fetch yet). */
  readonly bySku: Readonly<Record<string, SubstitutesEntry>>;
  /**
   * Lazily load `sku`'s substitutes (idempotent). A no-op while a fetch for that
   * SKU is already in flight (including a pending auto-retry) or already loaded —
   * UNLESS the active depot changed since the cached entry was fetched, in which
   * case the cached candidates are for the wrong depot and are refetched (the store
   * is depot-aware — see `SubstitutesEntry.depotId`). A terminal `error` entry is
   * re-tried only once its bounded retry window has expired — a re-invoke inside the
   * window keeps the terminal error (the ceiling is per-SKU-per-DEPOT-per-window, not
   * per-invocation). Resolves the session depot itself (the availability seam) so
   * callers just pass the SKU.
   */
  load: (sku: string) => Promise<void>;
  /**
   * Claim `sku`'s substitute work for as long as the caller wants it on screen. Always
   * pair with `release` in the same effect's cleanup, and call it BEFORE `load(sku)` so
   * the work is never unowned.
   *
   * REFCOUNTED, mirroring `useAvailabilityStore.retainSkus`/`releaseSkus`: entries here are
   * keyed by SKU so several surfaces share ONE fetch, and `ProductCard` renders from four
   * call sites (a listing, search, a recommendation rail, the upsell modal) — so two mounted
   * cards for the same SKU are ordinary, not exotic. Cancelling on the FIRST unmount tore
   * down a request the other card was still waiting on and cleared its loading state.
   */
  retain: (sku: string) => void;
  /**
   * Drop a `retain` claim. The LAST release — and only the last — cancels the SCHEDULING for
   * `sku`: any armed auto-retry is dropped and a request already on the wire will neither retry
   * nor write a `loading`/`error` state, so nothing is retried for a SKU no card is showing.
   * While another consumer still holds a claim this only decrements.
   *
   * It does NOT discard an ANSWER. A request already on the wire still commits a successful
   * response to the cache and still hands its budget back (see `runFetch`): the answer is the
   * endpoint's, not the watcher's, and throwing it away left the question settled with nothing to
   * show for it for a card that came back inside the flight window (round 18).
   *
   * On that last release the attempt count for the question is retained, so the bound still holds if
   * a card returns within the window, and an entry left in `loading` is dropped (never a
   * dead `loading` with nothing pending), so a later `load()` resumes the cycle rather than
   * early-returning on a stale phase. Tolerates a release with no matching claim.
   */
  release: (sku: string) => void;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

function withEntry(
  bySku: Readonly<Record<string, SubstitutesEntry>>,
  sku: string,
  entry: SubstitutesEntry,
): Readonly<Record<string, SubstitutesEntry>> {
  return { ...bySku, [sku]: entry };
}

// A monotonic generation stamp. `reset()` bumps it; a `load()` captures it before
// awaiting and only commits its result if the stamp is unchanged — so a fetch that
// resolves AFTER a reset() (logout / test hygiene) cannot resurrect a stale entry
// into the freshly-cleared cache. Mirrors the availability store's stamp guard.
let generation = 0;

// Story 7-FU-I (ADO-1222), AC4 — bounded AUTO-retry on a fetch error, on the shared ladder
// (`shared/bounded-retry.ts`, used by the substitute-candidate availability read too): the
// ceiling, backoff and per-key cooldown all live there. Keyed by SKU **and depot**, so the
// bound is per-QUESTION-per-window — ProductCard's load effect re-fires on remount and
// whenever a socket push toggles the band back to out-of-stock, so a per-invocation counter
// would let a hard-down endpoint be hammered without limit.
// The register owns BOTH halves of the bound — the issue count of the open cycle and the
// give-up stamp — so this store no longer keeps a count of its own beside it. The count
// persists across a re-invoke (that IS the bound) and is dropped on success, on cooldown
// expiry, and on `reset()`.
//
// Story 7-FU-K (ADO-1528), AC1/AC2/AC4 — this path answers the accounting question the SAME way
// the availability read cycle now does: the register counts REQUESTS PUT ON THE WIRE, recorded
// by `runFetch` before it calls `fetchSubstitutes`, with no `seen` and no compare-and-swap.
// Answering it two ways in one PR is what produced round 14, so the two must not diverge.
//
// It does NOT adopt the availability seam's cycle-owner module, and the asymmetry is deliberate
// (ADO-1528 D-3). This store is ALREADY the single owner of its own question: entries are keyed
// by SKU, `load()` early-returns while a `loading` entry exists, `retain`/`release` refcount the
// consumers and `cancelGeneration` retires an abandoned cycle — so there is no second driver for
// a cycle-owner module to displace. The availability question is different in kind: it is
// `candidate set + depot`, shared by cards that mount at different times, which is where offset
// observers could each drive accounting. What this path was missing is the narrower thing named
// as Item 1's third consequence — the per-attempt cooldown re-check, added in `runFetch`'s timer
// callback below.
//
// It also has NO `'superseded'` outcome to void, because there is no batching layer here that
// can decline to spend a request: every `runFetch` attempt reaches `fetchSubstitutes`, and a
// depot switch is handled by retiring the cycle (`cancelGeneration`) on a depot-keyed budget
// rather than by reporting an outcome back.
const retryRegister = createRetryRegister();
/**
 * The retry key: this SKU's substitutes AT THIS DEPOT. The depot belongs in it because
 * `fetchSubstitutes` is depot-scoped, so a switch asks a genuinely different question — and
 * because the accounting in `runFetch` is deliberately unconditional (a failure is the
 * endpoint's property, not the watcher's, so it is recorded even when the consumer is gone).
 * Under a SKU-only key those two rules combined against each other: a depot switch cancels
 * the old request and opens a fresh cycle, then the old request's failure lands and charges
 * the NEW depot's budget, which then gave up after two attempts instead of three (round 15,
 * F4). Keyed by depot, that failure lands on the question that actually failed.
 *
 * `retryTimers` is keyed by the SAME key, so an armed retry belongs to the question that armed
 * it. It was keyed by SKU alone until round 18, on the premise that at most one cycle per SKU is
 * live — but a cycle survives its own cancellation for as long as its request is on the wire, so
 * two cycles for one SKU overlap routinely (a remount inside the flight window; a depot switch
 * with the old request still out). Under the SKU key, whichever of them settled reached into the
 * other's timer: measured, an abandoned cycle's SUCCESS cancelled the live cycle's backoff and
 * left the card on `loading` with nothing pending, recoverable only by `reset()`.
 */
const retryKey = (sku: string, depotId: string | null): string => `${sku}|${depotId ?? ''}`;
/** Armed retries, keyed by QUESTION (`retryKey`). The `sku` is carried alongside rather than
 *  parsed back out of the key, so `release`'s "cancel everything for this SKU" needs no
 *  assumption about SKUs not containing the delimiter. */
let retryTimers: Record<
  string,
  { readonly sku: string; readonly timer: ReturnType<typeof setTimeout> }
> = {};
// Live consumer claims per SKU (`retain`/`release`). Entries are shared by SKU, so the
// question "is anyone still showing this?" cannot be answered by one card's unmount — see
// the `retain` doc above. Only the last release cancels. Mirrors `useAvailabilityStore`'s
// `retainedRefs`; kept in module scope alongside the other per-SKU bookkeeping so a
// store-level `reset()` clears it with everything else.
let consumerRefs: Record<string, number> = {};

// Per-SKU CANCEL generation. The last `release` bumps it; `runFetch` captures it at the start of
// its cycle and drops out before committing OR scheduling the next attempt if it has moved.
// Clearing the timer alone can't cover the common case: a card that unmounts while the
// INITIAL fetch is in flight has no timer yet, so without this token the fetch would reject
// and arm a fresh retry for the gone card, burning the bound. A re-invoking `load()` reads
// the bumped value, so its own attempts aren't seen as cancelled.
let cancelGeneration: Record<string, number> = {};

/** Cancel the retry armed for ONE question (`retryKey`). */
function clearRetryTimer(key: string): void {
  const armed = retryTimers[key];
  if (armed !== undefined) {
    clearTimeout(armed.timer);
    delete retryTimers[key];
  }
}

/**
 * Cancel every retry armed for `sku`, at whatever depot. Used where the decision is about the
 * SKU rather than about one question: the last consumer of the SKU leaving, and `load()` taking
 * ownership of the SKU's cycle at the current depot (a retry still armed at a depot the app has
 * left has no consumer to retry for — its budget stays in the register either way).
 */
function clearRetryTimersForSku(sku: string): void {
  for (const [key, armed] of Object.entries(retryTimers)) {
    if (armed.sku === sku) clearRetryTimer(key);
  }
}

export const useSubstitutesStore = create<SubstitutesStoreState>((set, get) => {
  /**
   * The single gate on "may a request still go on the wire for this question?" — Story 7-FU-K
   * (ADO-1528), AC2 and Item 1's third consequence. Returns `true` when the request is REFUSED,
   * and materialises the terminal state for whoever is asking.
   *
   * Reached from BOTH entry points, which is the whole point: `load()` and the retry timer. The
   * timer used to call `runFetch` directly, testing only `gen` and `cancelled()` — neither of
   * which moves when the QUESTION is spent rather than reset or abandoned — so it was the one
   * way into `runFetch` that skipped the budget. The availability read cycle has always
   * re-checked per attempt because its timer re-enters the same decision function; routing this
   * timer through the same gate is that property rather than a second, parallel check.
   *
   * WHY THE ISSUE COUNT AND NOT JUST THE COOLDOWN. A cooldown stamp only exists once a cycle has
   * reached the ceiling AND its terminal response has landed — and a cycle CANCELLED by a depot
   * switch can still be holding the wire when the user switches back, because cancellation
   * suppresses scheduling, not a request already sent. So the question's count is at the ceiling
   * with no stamp beside it, and the old cooldown-only check read `'none'` and issued a FOURTH
   * request. Stamping the give-up here bounds the question and lets it age out, rather than
   * leaving it permanently unaskable with no stamp to expire.
   */
  function refuseIfSpent(sku: string, depotId: string | null, key: string): boolean {
    // Destructive on expiry, and safe for the same reason it is in the read cycle: this function
    // is the register's only reader (ADO-1528 AC5). Read ONCE and branch on the value: reading
    // twice worked only because the first read mutated — the second then saw `'none'` where the
    // first saw `'expired'` — which is a correct answer arrived at by accident, and the kind of
    // thing the next edit here breaks silently. An expired window takes its count with it inside
    // `status`, so the `issued` check below sees a genuinely fresh ladder.
    const cooldown = retryRegister.status(key);
    // Scoped to `key`, not to the SKU: the stale timer an expired window may have left behind is
    // this question's own, and a retry armed for a DIFFERENT depot's cycle is that cycle's to
    // cancel. Clearing by SKU here reached across questions exactly as the success path did
    // (round 18).
    if (cooldown === 'expired') clearRetryTimer(key);
    if (cooldown !== 'cooling' && retryRegister.issued(key) < MAX_RETRY_ATTEMPTS) {
      return false;
    }
    retryRegister.gaveUp(key); // no-op inside an existing window — the original stamp stands
    // MATERIALISE the terminal state for whoever is asking now. Because the bound is accounted
    // even when no consumer survives the response (see `runFetch`), the cycle that spent the
    // ladder may have finished with nothing on screen and so written no entry — `release` had
    // already dropped its `loading` one. Without this the card would render a Substitute CTA with
    // no status text at all for the rest of the window, which reads as broken rather than as
    // "could not be loaded".
    //
    // Stamped at the CURRENT depot deliberately: the spent key IS this depot's, so this cannot
    // write an entry for the depot an abandoned cycle was fetching at.
    //
    // The DEPOT is part of "is the terminal state already on screen?", not just the phase. An
    // `error` entry stamped at another depot is invisible here — `atDepot` returns undefined for
    // it, so the CTA renders no status text at all — and skipping the write on the phase alone
    // left exactly that: spend the ladder at A, spend it at B, come back to A inside its window,
    // and the card showed a Substitute CTA with no explanation for the rest of the cooldown. That
    // is the failure this block exists to prevent, reached through the entry it was checking.
    const shown = get().bySku[sku];
    if (shown?.phase !== 'error' || shown.depotId !== depotId) {
      set({ bySku: withEntry(get().bySku, sku, { phase: 'error', substitutes: [], depotId }) });
    }
    return true;
  }

  // One fetch attempt for `sku`. `gen` is the reset generation captured at the START of the
  // load cycle; a reset() bumps it so a result (or a scheduled retry) that lands afterwards
  // is dropped and cannot resurrect a stale entry. `cancelGen` is the per-SKU cancel
  // generation captured the same way: the LAST release() bumps it so a fetch that settles
  // after the final consumer is gone can neither commit nor arm a fresh retry.
  async function runFetch(
    sku: string,
    entry: string,
    depotId: string | null,
    gen: number,
    cancelGen: number,
  ): Promise<void> {
    const cancelled = (): boolean => cancelGen !== (cancelGeneration[sku] ?? 0);
    const key = retryKey(sku, depotId);
    // ACCOUNTING, taken HERE — before the request goes out, because what the ceiling bounds is
    // requests put on the WIRE (ADO-1528 AC2). Nothing can discard it and nobody's departure can
    // un-count it: the request was spent.
    const issued = retryRegister.recordIssue(key);
    try {
      // `entry` and `depotId` are captured at load() and threaded through so every attempt in
      // a cycle (and its stamped entry) asks the SAME question; a live switch re-invokes
      // load(), which detects the change and starts a fresh cycle at the new depot, and an
      // entry change resets the store (`startCatalogueEntryResets`), dropping this cycle.
      const substitutes = await fetchSubstitutes(entry, sku);
      if (gen !== generation) return; // a reset() happened mid-fetch — drop the stale result
      // A SUCCESS ENDS THE QUESTION, whoever is still watching. A success is a property of the
      // ENDPOINT, exactly as a failure is (see the catch below), so all three acts below are
      // unconditional: the budget goes back, the armed retry is cancelled, and the answer is
      // written. Leaving the budget behind leaked one request per
      // mount-load-release-before-response cycle once the count moved to ISSUE time — nothing else
      // calls `forget` on this path, so three such cycles reached the ceiling and `refuseIfSpent`
      // wrote a terminal error and a 30s cooldown for a SKU whose every fetch had SUCCEEDED.
      //
      // Every unconditional act here is scoped to THIS QUESTION — `key` carries the depot, and
      // `retryTimers` is keyed by the same `key` — which is what makes it safe to run them for a
      // cycle no consumer is waiting on. That is the property the availability owner has (its
      // unconditional act on `'answered'` is `register.forget(cycle.key)`, which touches only its
      // own question) and the property this path did NOT have in round 18: `clearRetryTimer` was
      // keyed by SKU alone, so an abandoned cycle's response cancelled the retry of whichever
      // cycle owned that SKU at the time.
      retryRegister.forget(key);
      clearRetryTimer(key);
      // COMMITTING THE ANSWER IS UNCONDITIONAL TOO, and for the same reason: the answer is the
      // ENDPOINT's, not the watcher's. It is guarded only against writing over a DIFFERENT
      // question — a depot switch opens a fresh cycle at the new depot and stamps the slot with
      // it, and this cycle's answer belongs to the depot it was fetched at.
      //
      // Gating this on `cancelled()` is what produced the two shapes measured in round 18. The
      // cycle above has just forgotten the budget and cancelled the retry for this question —
      // both correct, the endpoint answered it — so declining to write the answer leaves the
      // question settled with nothing to show for it: a card that remounted inside the flight
      // window sat on `loading` with no timer, no request and no cooldown, and `load()`
      // early-returns on `loading`, so only `reset()` recovered it (2 wire calls, entry
      // `loading`). Clearing less does not fix that — it converts the stall into re-laddering a
      // question that has an answer (measured: 8 calls against a ceiling of 3).
      const slot = get().bySku[sku];
      if (slot !== undefined && slot.depotId !== depotId) return; // a newer cycle owns the slot
      set({ bySku: withEntry(get().bySku, sku, { phase: 'loaded', substitutes, depotId }) });
    } catch {
      if (gen !== generation) return; // a reset() happened mid-fetch — drop the stale error
      // THE QUESTION MAY HAVE BEEN ANSWERED WHILE THIS REQUEST WAS OUT, by the other cycle. Two
      // cycles for one question overlap whenever a consumer leaves and comes back inside the
      // flight window (a remount, StrictMode's setup → cleanup → setup), because a cycle survives
      // its own cancellation for as long as its request is on the wire. If the other one
      // succeeded it released the budget, so climbing the ladder from here re-asks a question that
      // already has an answer and spends rungs nothing has paid for: measured, 5 wire requests
      // against a ceiling of 3 (round 18). The committed entry is read rather than a flag of our
      // own precisely so the two cannot drift — the answer and the budget release are written on
      // one synchronous path in the `try` above.
      const answered = get().bySku[sku];
      if (answered?.phase === 'loaded' && answered.depotId === depotId) return;
      // The request was already counted before it went out (see `recordIssue` above), so the
      // only thing left to decide is TERMINALITY — and that is "the ladder has no rung for the
      // requests already spent" rather than a separate comparison, which is what makes it
      // impossible to schedule a retry on an undefined rung (ADO-1528 AC4).
      //
      // Both halves stay UNCONDITIONAL. A failure is a property of the ENDPOINT, not of whoever
      // happened to be watching, so the give-up stamp that puts the SKU in cooldown is recorded
      // whether or not a consumer survived to see the response. Skipping the accounting when
      // cancelled left the bound unsettled by exactly the consumer that goes away mid-flight,
      // which is the common case (a card unmounts while the first fetch is in flight, before any
      // timer exists). Measured before this rule: 6 retain → load → release-before-response
      // cycles against a hard-down endpoint produced 6 fetches against a ceiling of 3.
      //
      // The same rule holds in the availability read cycle, the other consumer of this ladder.
      // The two used to answer this question differently — one skipped the count when
      // cancelled, the other skipped the give-up stamp — so each leaked at its own boundary.
      //
      // What makes the rule safe here is that `key` carries the DEPOT: this records against
      // the question that actually failed, so a cancelled old-depot request can no longer
      // charge the fresh cycle a depot switch just opened (round 15, F4 — see `retryKey`).
      const delay = nextBackoffMs(issued);
      if (delay === undefined) retryRegister.gaveUp(key);

      // SCHEDULING AND STATE WRITES — conditional. A cancelled cycle (card unmounted /
      // trigger off) must not fire a retry against an endpoint nobody is waiting on, and has
      // no entry to write: `release` dropped it so a re-invoke re-enters. The accounting
      // above already stands for whoever asks next.
      if (cancelled()) return;
      if (delay !== undefined) {
        // A retry is pending → stay in `loading`. Do NOT flash `error` here: the CTA maps
        // `error` to "Substitutes could not be loaded", so writing it mid-backoff would
        // surface a failure that's about to be retried, the opposite of AC4's quiet
        // recovery. The entry is already `loading`, so leave it.
        retryTimers[key] = {
          sku,
          timer: setTimeout(() => {
            delete retryTimers[key];
            if (gen !== generation) return; // reset() during the backoff — abandon the retry
            if (cancelled()) return; // cancelled during the backoff — abandon the retry
            // The PER-ATTEMPT budget re-check the availability read cycle has always had and this
            // path never did (ADO-1528, Item 1's third consequence). This was the one way into
            // `runFetch` that bypassed the gate `load()` goes through.
            if (refuseIfSpent(sku, depotId, key)) return;
            void runFetch(sku, entry, depotId, gen, cancelGen);
          }, delay),
        };
        return;
      }
      // Terminal: the ceiling is reached, no further retry is scheduled, and the entry goes
      // to the QUIET terminal `error` state (no manual CTA — the AC4 PO decision). The
      // attempt count is RETAINED so a re-invoke inside the cooldown respects the bound; both
      // expire via the cooldown check in load().
      set({ bySku: withEntry(get().bySku, sku, { phase: 'error', substitutes: [], depotId }) });
    }
  }

  return {
    bySku: {},

    async load(sku) {
      // Enabler E7-C (ADO-2274), D-1: the candidates are read in the selected entry's range.
      // With no entry there is nothing to ask; the card re-invokes `load` once one is chosen.
      const entry = resolveCatalogueEntryKey();
      if (entry === null) return;
      const depotId = resolveSessionDepotId();
      const existing = get().bySku[sku];
      // Depot-aware refetch: a header depot switch flips the active depot WITHOUT resetting
      // this store (only DepotPicker.handleChange resets it), and entries are keyed by SKU
      // alone — so a cached `loaded`/`loading` entry for the OLD depot would
      // early-return below and leave the card showing that depot's candidates while the OOS
      // filter evaluates NEW-depot stock. When the depot differs the cache is stale: abandon
      // any in-flight fetch / pending retry for the old depot (bump the cancel generation) and
      // drop its timer, so this is a fresh, fully-bounded cycle at the new depot.
      //
      // This branch never forgets the old depot's LADDER: it is keyed by the question it
      // belongs to (`retryKey`), so it cannot bleed into the new depot's budget, and leaving
      // it standing means a switch straight back inside the window still finds that question
      // spent instead of re-laddering an endpoint that just failed three times. That holds on
      // the HEADER switch path (`useHeaderChrome` → `setActiveDepot`), which is the path that
      // reaches this branch at all — it moves the depot without touching this store.
      // `DepotPicker.handleChange` takes the other path: it calls `reset()` first
      // (`DepotPicker.tsx:53`), and `reset()` clears the whole register, so on that path every
      // key's count and stamp go with it and the next load starts a genuinely fresh ladder.
      const depotChanged = existing !== undefined && existing.depotId !== depotId;
      if (depotChanged) {
        cancelGeneration[sku] = (cancelGeneration[sku] ?? 0) + 1;
        clearRetryTimersForSku(sku);
      } else if (existing?.phase === 'loading' || existing?.phase === 'loaded') {
        // Skip a redundant fetch: in flight (including a pending auto-retry, which
        // keeps the entry in `loading`) or already resolved at the CURRENT depot.
        return;
      }

      // Here the entry is absent, a terminal `error`, or stale-for-this-depot. Honour the
      // per-QUESTION bound, through the one gate both entry points use (`refuseIfSpent`). The
      // cooldown is stamped at the TERMINAL failure, so it means "N seconds since we last gave up
      // on THIS SKU AT THIS DEPOT" regardless of how slow the attempts were — and because the
      // depot is in the key, a switch reaches the gate on its own question: normally 'none' (a
      // fresh ladder), but 'cooling' if we gave up on this very depot moments ago and the user has
      // switched back, and REFUSED-with-no-stamp if a cancelled cycle at this depot has already
      // spent the wire budget and its terminal response has yet to land.
      //
      // Reaching the gate at all means no live cycle holds the SKU at this depot — a `loading` or
      // `loaded` entry at this depot returned above, and a depot-mismatched one had its cycle
      // cancelled — so the terminal entry it may write cannot belong to another depot's cycle.
      const key = retryKey(sku, depotId);
      if (refuseIfSpent(sku, depotId, key)) return;
      // Whatever falls through — a fresh SKU, an expired-and-forgotten one, or a mid-cycle
      // RESUME — reads its attempts from the register. A resume matters: the last release
      // drops a `loading` entry but RETAINS its attempts (no cooldown yet, since we never hit
      // the terminal), so the resumed cycle stays bounded rather than restarting at zero.
      clearRetryTimersForSku(sku);
      const gen = generation;
      const cancelGen = cancelGeneration[sku] ?? 0;
      // Nothing is threaded into `runFetch` about how much of the ladder is spent: it reads that
      // from the register itself when it records its own request. A resumed cycle therefore picks
      // the ladder up where the abandoned one left it, and an expired one starts from zero
      // because the status check above dropped that window's count.
      // Always replace the entry with `loading` at the CURRENT depot — never keep the OLD
      // depot's candidates on screen, which ProductCard would (correctly) discard anyway.
      // The CTA flash across a depot-change refetch is handled instead by marking the entry
      // `refetching` when it displaced a list the card was ALREADY offering, so the card can
      // hold a pending, non-actionable CTA through the round trip. A FIRST load carries no
      // flag, so a card with nothing on screen yet still shows nothing.
      const displaced = get().bySku[sku];
      const refetching =
        depotChanged && displaced?.phase === 'loaded' && displaced.substitutes.length > 0;
      set({
        bySku: withEntry(get().bySku, sku, {
          phase: 'loading',
          substitutes: [],
          depotId,
          ...(refetching ? { refetching: true } : {}),
        }),
      });
      await runFetch(sku, entry, depotId, gen, cancelGen);
    },

    retain(sku) {
      consumerRefs[sku] = (consumerRefs[sku] ?? 0) + 1;
    },

    release(sku) {
      const count = consumerRefs[sku];
      // Tolerate a release with no matching claim (a reset() between retain and cleanup):
      // never let the count go negative and never throw on the way out.
      if (count === undefined) return;
      if (count > 1) {
        consumerRefs[sku] = count - 1;
        // Another mounted consumer still wants this SKU's substitutes: leave its request,
        // its pending retry and its `loading` entry exactly as they are.
        return;
      }
      delete consumerRefs[sku];

      // Last consumer gone. Bump the cancel generation so ANY in-flight work for this SKU —
      // a scheduled retry OR an initial fetch that hasn't armed its timer yet — is abandoned
      // when it settles. The attempt count + cooldown stamp stay, so the bound still holds
      // on remount.
      cancelGeneration[sku] = (cancelGeneration[sku] ?? 0) + 1;
      clearRetryTimersForSku(sku); // every depot's — no consumer is left for any of them
      // A cancelled entry left in `loading` with no pending timer and no live fetch would
      // dead-end the card on a permanent spinner: load()'s `loading` guard early-returns with
      // nothing left to resolve it but reset(). Drop it so a re-invoke RE-ENTERS load() and
      // resumes under the RETAINED per-question bound. A `loaded`/terminal-`error` entry is
      // settled work with nothing pending — leave it intact.
      if (get().bySku[sku]?.phase === 'loading') {
        const next = { ...get().bySku };
        delete next[sku];
        set({ bySku: next });
      }
    },

    reset() {
      generation += 1;
      retryRegister.clear();
      cancelGeneration = {};
      consumerRefs = {};
      for (const { timer } of Object.values(retryTimers)) clearTimeout(timer);
      retryTimers = {};
      set({ bySku: {} });
    },
  };
});
